import { useState } from "react";

import { useTagAnalytics } from "../api/hooks/useTagAnalytics";
import type { components } from "../api/schema";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useGlobalFilters } from "../lib/globalFilters";
import { formatMoney } from "../lib/money";
import { cn } from "../lib/cn";

type TagAnalyticsResponse = components["schemas"]["TagAnalyticsResponse"];
type TagAnalyticsView = TagAnalyticsResponse["by_tag"];
type TagBucket = TagAnalyticsView["buckets"][number];
type TagTimeMatrix = TagAnalyticsResponse["by_tag_time_of_day"];
type TagTimeCell = TagTimeMatrix["cells"][number];
type AutoInsights = TagAnalyticsView["auto_insights"];

const VIEW_IDS = ["bar", "bubble", "heatmap", "grid"] as const;
type ViewId = (typeof VIEW_IDS)[number];

const VIEW_LABELS: Record<ViewId, string> = {
  bar: "Wins / Losses Bar",
  bubble: "Big-PnL Bubble",
  heatmap: "Tag Heatmap",
  grid: "Tag x Time-of-Day Grid",
};

const TIME_BUCKETS = ["Pre-market", "Open", "Mid-day", "Close", "After-hours"] as const;

type Nullable = string | null | undefined;

function safeFormatMoney(amount: Nullable, currency: Nullable): string {
  if (amount === null || amount === undefined) return "—";
  if (currency === null || currency === undefined) return "—";
  try {
    return formatMoney(amount, { currency });
  } catch {
    return `${amount} ${currency}`;
  }
}

function formatPercent(decimal: Nullable, options: { decimals?: number } = {}): string {
  if (decimal === null || decimal === undefined) return "—";
  const n = Number(decimal);
  if (Number.isNaN(n)) return decimal;
  const decimals = options.decimals ?? 1;
  return `${(n * 100).toFixed(decimals)}%`;
}

function formatInteger(n: number): string {
  return n.toLocaleString("en-US");
}

interface BarChartProps {
  buckets: readonly TagBucket[];
}

/**
 * Per-tag stacked wins (emerald) + scratches (slate) + losses (rose)
 * row. Reuses the V2-U12 WinRateBarChart shape with tag labels.
 */
function TagBarChart({ buckets }: BarChartProps) {
  if (buckets.length === 0) {
    return (
      <div className="text-sm text-on-surface-variant" data-testid="tag-analytics-bars-empty">
        No data in the selected range.
      </div>
    );
  }
  const maxCount = buckets.reduce((acc, b) => (b.trade_count > acc ? b.trade_count : acc), 0);
  return (
    <div className="space-y-1" data-testid="tag-analytics-bars">
      {buckets.map((bucket) => {
        const widthPct = maxCount > 0 ? (bucket.trade_count / maxCount) * 100 : 0;
        const winsPct =
          bucket.trade_count > 0 ? (bucket.wins / bucket.trade_count) * widthPct : 0;
        const lossesPct =
          bucket.trade_count > 0 ? (bucket.losses / bucket.trade_count) * widthPct : 0;
        const scratchesPct =
          bucket.trade_count > 0 ? (bucket.scratches / bucket.trade_count) * widthPct : 0;
        return (
          <div
            key={bucket.label}
            className="grid grid-cols-[10rem_1fr_8rem] items-center gap-2 text-sm"
            data-testid={`tag-analytics-bars-row-${bucket.label}`}
          >
            <div className="truncate font-medium text-on-surface" title={bucket.label}>
              {bucket.label}
            </div>
            <div className="relative h-4 bg-surface-container-high">
              {winsPct > 0 && (
                <div
                  className="absolute inset-y-0 bg-success"
                  style={{ left: 0, width: `${winsPct}%` }}
                />
              )}
              {scratchesPct > 0 && (
                <div
                  className="absolute inset-y-0 bg-on-surface-muted"
                  style={{ left: `${winsPct}%`, width: `${scratchesPct}%` }}
                />
              )}
              {lossesPct > 0 && (
                <div
                  className="absolute inset-y-0 bg-danger"
                  style={{ left: `${winsPct + scratchesPct}%`, width: `${lossesPct}%` }}
                />
              )}
            </div>
            <div className="text-right tabular-nums text-on-surface">
              {formatPercent(bucket.win_rate, { decimals: 1 })}
              <span className="ml-2 text-xs text-on-surface-variant">
                {formatInteger(bucket.trade_count)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface BubbleChartProps {
  buckets: readonly TagBucket[];
  currency: string;
}

/**
 * Each tag renders as a CSS disc sized proportionally to
 * ``|total_pnl|`` and colored by sign (emerald=positive, rose=negative,
 * slate=zero). +0 SVG, +0 chart libs.
 */
function TagBubbleChart({ buckets, currency }: BubbleChartProps) {
  if (buckets.length === 0) {
    return (
      <div className="text-sm text-on-surface-variant" data-testid="tag-analytics-bubbles-empty">
        No data in the selected range.
      </div>
    );
  }
  const maxAbs = buckets.reduce((acc, b) => {
    const v = Math.abs(Number(b.total_pnl_amount));
    return v > acc ? v : acc;
  }, 0);
  return (
    <div className="flex flex-wrap items-end gap-4" data-testid="tag-analytics-bubbles">
      {buckets.map((bucket) => {
        const amount = Number(bucket.total_pnl_amount);
        const ratio = maxAbs > 0 ? Math.abs(amount) / maxAbs : 0;
        const diameterPx = Math.max(24, Math.round(20 + 80 * ratio));
        const color = amount > 0 ? "bg-success" : amount < 0 ? "bg-danger" : "bg-on-surface-muted";
        return (
          <div
            key={bucket.label}
            className="flex flex-col items-center gap-1 text-xs text-on-surface"
            data-testid={`tag-analytics-bubbles-row-${bucket.label}`}
          >
            <div
              className={cn("rounded-full opacity-70", color)}
              style={{ width: `${diameterPx}px`, height: `${diameterPx}px` }}
              title={`${bucket.label}: ${safeFormatMoney(bucket.total_pnl_amount, currency)}`}
            />
            <span className="font-medium">{bucket.label}</span>
            <span className="tabular-nums text-on-surface-variant">
              {safeFormatMoney(bucket.total_pnl_amount, currency)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

interface MatrixProps {
  cells: readonly TagTimeCell[];
  mode: "heatmap" | "grid";
}

/**
 * Render the 2D ``(tag, session)`` cells as an HTML table.
 *
 * - ``heatmap`` mode: colors each cell by ``win_rate`` intensity
 *   (greener = higher; rose = lower; slate = empty).
 * - ``grid`` mode: shows ``wins / losses`` plus the trade count
 *   per cell (no color encoding).
 */
function TagTimeMatrix({ cells, mode }: MatrixProps) {
  if (cells.length === 0) {
    return (
      <div className="text-sm text-on-surface-variant" data-testid={`tag-analytics-${mode}-empty`}>
        No data in the selected range.
      </div>
    );
  }
  // Group cells into rows keyed by tag_label (row order matches the
  // cell order, which the backend already sorts).
  const rowOrder: string[] = [];
  const byRow = new Map<string, TagTimeCell[]>();
  for (const cell of cells) {
    if (!byRow.has(cell.tag_label)) {
      rowOrder.push(cell.tag_label);
      byRow.set(cell.tag_label, []);
    }
    byRow.get(cell.tag_label)!.push(cell);
  }
  return (
    <table
      className="w-full border-separate border-spacing-1 text-sm"
      data-testid={`tag-analytics-${mode}`}
    >
      <thead>
        <tr>
          <th className="text-left font-medium text-on-surface-variant">Tag</th>
          {TIME_BUCKETS.map((session) => (
            <th key={session} className="text-center font-medium text-on-surface-variant">
              {session}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rowOrder.map((row) => (
          <tr key={row} data-testid={`tag-analytics-${mode}-row-${row}`}>
            <td className="truncate pr-2 font-medium text-on-surface" title={row}>
              {row}
            </td>
            {(byRow.get(row) ?? []).map((cell) => (
              <td
                key={cell.time_bucket}
                className={cn(
                  "rounded text-center tabular-nums",
                  mode === "heatmap" ? heatmapTone(cell.win_rate) : "bg-surface",
                )}
                data-testid={`tag-analytics-${mode}-cell-${row}-${cell.time_bucket}`}
              >
                {mode === "heatmap"
                  ? formatPercent(cell.win_rate, { decimals: 0 })
                  : `${cell.wins}W / ${cell.losses}L (${formatInteger(cell.trade_count)})`}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function heatmapTone(winRate: Nullable): string {
  if (winRate === null || winRate === undefined) return "bg-surface-container-high text-on-surface-muted";
  const n = Number(winRate);
  if (Number.isNaN(n)) return "bg-surface-container-high text-on-surface-muted";
  if (n >= 0.75) return "bg-success text-on-primary";
  if (n >= 0.5) return "bg-success-soft text-success";
  if (n >= 0.25) return "bg-danger-soft text-danger";
  return "bg-danger text-on-primary";
}

interface InsightsProps {
  insights: AutoInsights;
  currency: string;
  testId: string;
}

function AutoInsightsPanel({ insights, currency, testId }: InsightsProps) {
  const bestText =
    insights.best === null || insights.best === undefined
      ? "—"
      : `${insights.best.label}: ${safeFormatMoney(insights.best.total_pnl_amount, currency)}`;
  const worstText =
    insights.worst === null || insights.worst === undefined
      ? "—"
      : `${insights.worst.label}: ${safeFormatMoney(insights.worst.total_pnl_amount, currency)}`;
  const bestWinrateText =
    insights.best_winrate === null || insights.best_winrate === undefined
      ? "—"
      : `${insights.best_winrate.label}: ${formatPercent(insights.best_winrate.win_rate, { decimals: 1 })}`;
  const worstWinrateText =
    insights.worst_winrate === null || insights.worst_winrate === undefined
      ? "—"
      : `${insights.worst_winrate.label}: ${formatPercent(insights.worst_winrate.win_rate, { decimals: 1 })}`;
  return (
    <Card data-testid={testId}>
      <CardHeader>
        <CardTitle>Auto-Insights</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-wide text-on-surface-variant">Best by P&amp;L</span>
          <span
            className="font-medium text-success"
            data-testid={`${testId}-best`}
          >
            {bestText}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-wide text-on-surface-variant">Worst by P&amp;L</span>
          <span className="font-medium text-danger" data-testid={`${testId}-worst`}>
            {worstText}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-wide text-on-surface-variant">Best win rate</span>
          <span
            className="font-medium text-success"
            data-testid={`${testId}-best-winrate`}
          >
            {bestWinrateText}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-wide text-on-surface-variant">Worst win rate</span>
          <span
            className="font-medium text-danger"
            data-testid={`${testId}-worst-winrate`}
          >
            {worstWinrateText}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs uppercase tracking-wide text-on-surface-variant">Win-rate spread</span>
          <span data-testid={`${testId}-winrate-divergence`}>
            {formatPercent(insights.winrate_divergence_pct, { decimals: 1 })}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

interface ViewProps {
  data: TagAnalyticsResponse;
  viewId: ViewId;
}

function ChartView({ data, viewId }: ViewProps) {
  const oneDView = data.by_tag;
  const matrixView = data.by_tag_time_of_day;
  const currency = data.currency;
  const insightsView = viewId === "heatmap" || viewId === "grid" ? matrixView : oneDView;
  return (
    <div
      className="grid gap-4 lg:grid-cols-[2fr_1fr]"
      data-testid={`tag-analytics-panel-${viewId}`}
    >
      <Card>
        <CardHeader>
          <CardTitle>{VIEW_LABELS[viewId]}</CardTitle>
        </CardHeader>
        <CardContent>
          {viewId === "bar" && <TagBarChart buckets={oneDView.buckets} />}
          {viewId === "bubble" && (
            <TagBubbleChart buckets={oneDView.buckets} currency={currency} />
          )}
          {viewId === "heatmap" && <TagTimeMatrix cells={matrixView.cells} mode="heatmap" />}
          {viewId === "grid" && <TagTimeMatrix cells={matrixView.cells} mode="grid" />}
        </CardContent>
      </Card>
      <AutoInsightsPanel
        insights={insightsView.auto_insights}
        currency={currency}
        testId={`tag-analytics-insights-${viewId}`}
      />
    </div>
  );
}

export function TagAnalyticsPage() {
  const globalFilters = useGlobalFilters();
  const [activeView, setActiveView] = useState<ViewId>("bar");

  // Date/account/tag scope comes from the shared Analytics FilterBar
  // (AnalyticsLayout), which writes the same URL search this page reads. To view
  // all-time, clear the date in that FilterBar.
  const tagsQuery = useTagAnalytics(globalFilters);

  return (
    <main className="space-y-6 p-6">
      <header className="flex items-baseline justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
            Tag Performance
          </h1>
          <p className="text-sm text-on-surface-variant">
            Per-tag performance breakdown plus a 2D tag x time-of-day matrix.
          </p>
        </div>
      </header>

      <nav
        className="flex flex-wrap gap-2 border-b border-outline-variant"
        role="tablist"
        aria-label="Tag analytics views"
      >
        {VIEW_IDS.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={activeView === id}
            data-testid={`view-tab-${id}`}
            onClick={() => setActiveView(id)}
            className={cn(
              "rounded-t-md border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              activeView === id
                ? "border-primary text-on-surface"
                : "border-transparent text-on-surface-variant hover:text-on-surface",
            )}
          >
            {VIEW_LABELS[id]}
          </button>
        ))}
      </nav>

      {tagsQuery.isLoading && (
        <div className="text-sm text-on-surface-variant" data-testid="tag-analytics-loading">
          Loading tag analytics…
        </div>
      )}
      {tagsQuery.error !== null && tagsQuery.error !== undefined && (
        <div className="text-sm text-error" data-testid="tag-analytics-error">
          Failed to load tag analytics. Try changing the filter and reloading.
        </div>
      )}
      {tagsQuery.data !== undefined && (
        <section
          role="tabpanel"
          aria-labelledby={`view-tab-${activeView}`}
          data-testid={`view-panel-${activeView}`}
        >
          <ChartView data={tagsQuery.data} viewId={activeView} />
        </section>
      )}
    </main>
  );
}
